<script setup>
import { ref, onMounted, onUnmounted, computed, nextTick } from 'vue';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import CustomLink from './CustomLink.vue';
import Button from './Button.vue';

gsap.registerPlugin(ScrollTrigger);

const props = defineProps({
  products: {
    type: Array,
    required: true,
  },
  title: {
    type: String,
    default: "Slim, schaalbaar en krachtig.",
  },
  subtitle: {
    type: String,
    default: "Onze projecten laten zien hoe flexibele software echte resultaten oplevert – en hoe wij jou kunnen helpen om meer te verbinden, minder te verspillen en slimmer te werken."
  }
});

// Refs for animation and navigation
const bentoContainerRef = ref(null);
const showcaseRef = ref(null);
const titleRef = ref(null);
const subtitleRef = ref(null);
const buttonRef = ref(null);
const currentSet = ref(0);
const totalSets = ref(0);
const isAnimating = ref(false);

// Mouse tracking for cursor-following arrow
const mouseX = ref(0);
const mouseY = ref(0);
const isHovering = ref(false);
const currentCard = ref(null);

// Calculate how many product sets we can display
const calculateTotalSets = () => {
  // We need at least 3 products for a complete set
  totalSets.value = Math.ceil(props.products.length / 3);
};

// Get the current set of 3 products to display
const currentProducts = computed(() => {
  const startIdx = currentSet.value * 3;
  return props.products.slice(startIdx, startIdx + 3);
});

// Helper to ensure we always have 3 items per set (use placeholder for incomplete sets)
const displayProducts = computed(() => {
  const products = [...currentProducts.value];
  while (products.length < 3) {
    // Add placeholder if needed
    products.push({ 
      meta: { id: `placeholder-${products.length}`, name: 'Placeholder' },
      style: { projectColor: 'gray' },
      content: { subTitle: 'Placeholder item', hoverCta: 'Meer info' },
      isPlaceholder: true
    });
  }
  return products;
});

// Navigation functions - updated for smoother transitions that don't block UI
const navigatePrev = () => {
  if (currentSet.value > 0 && !isAnimating.value) {
    // Set animating state but allow quick successive clicks
    isAnimating.value = true;
    
    // Start animation immediately
    gsap.to(bentoContainerRef.value, { 
      opacity: 0.5,
      duration: 0.15,
      onComplete: () => {
        // Update current set
        currentSet.value--;
        
        // Immediately fade in new content
        gsap.to(bentoContainerRef.value, { 
          opacity: 1, 
          duration: 0.15,
          onComplete: () => { 
            isAnimating.value = false; 
          }
        });
      }
    });
    
    // Reset animation state quickly to allow button clicks
    setTimeout(() => {
      isAnimating.value = false;
    }, 150);
  }
};

const navigateNext = () => {
  if (currentSet.value < totalSets.value - 1 && !isAnimating.value) {
    // Set animating state but allow quick successive clicks
    isAnimating.value = true;
    
    // Start animation immediately
    gsap.to(bentoContainerRef.value, { 
      opacity: 0.5,
      duration: 0.15,
      onComplete: () => {
        // Update current set
        currentSet.value++;
        
        // Immediately fade in new content
        gsap.to(bentoContainerRef.value, { 
          opacity: 1, 
          duration: 0.15,
          onComplete: () => { 
            isAnimating.value = false; 
          }
        });
      }
    });
    
    // Reset animation state quickly to allow button clicks
    setTimeout(() => {
      isAnimating.value = false;
    }, 150);
  }
};

// Helper function to get image for a product
const getProductImage = (product) => {
  if (product.isPlaceholder) return '';
  
  // First priority: use sliderImage if available
  if (product.media?.sliderImage) {
    return product.media.sliderImage;
  }
  
  // Second priority: fallback to showcase images
  if (product.sections?.showcase && Object.keys(product.sections.showcase).length > 0) {
    const showcaseKeys = Object.keys(product.sections.showcase);
    const randomKey = showcaseKeys[Math.floor(Math.random() * showcaseKeys.length)];
    return product.sections.showcase[randomKey].image;
  }
  
  // Third priority: use mockup image
  if (product.media?.mockupImages?.mid?.src) {
    return product.media.mockupImages.mid.src;
  }
  
  // Final fallback
  return 'https://images.unsplash.com/photo-1511376777868-611b54f68947';
};

// Helper function to split text on commas
const splitTextOnComma = (text) => {
  return text ? text.split(',').map((line, i, arr) => i < arr.length - 1 ? `${line.trim()},` : line.trim()) : [];
};

// Track mouse position within card
const trackMousePosition = (event, cardIndex) => {
  const rect = event.currentTarget.getBoundingClientRect();
  mouseX.value = event.clientX - rect.left;
  mouseY.value = event.clientY - rect.top;
  isHovering.value = true;
  currentCard.value = cardIndex;
};

// Reset hover state when leaving card
const resetHover = () => {
  isHovering.value = false;
  currentCard.value = null;
};

onMounted(() => {
  // Calculate how many sets of products we have
  calculateTotalSets();
  
  // Animate title and subtitle with smooth fade-in from bottom - matching ServiceTabs style
  gsap.fromTo(
    titleRef.value,
    { opacity: 0, y: 30 },
    { 
      opacity: 1, 
      y: 0,
      duration: 0.8,
      ease: "power2.out",
      scrollTrigger: {
        trigger: showcaseRef.value,
        start: "top 80%",
        toggleActions: "play none none none"
      }
    }
  );
  
  gsap.fromTo(
    subtitleRef.value,
    { opacity: 0, y: 30 },
    { 
      opacity: 1, 
      y: 0,
      duration: 0.8,
      delay: 0.2,
      ease: "power2.out",
      scrollTrigger: {
        trigger: showcaseRef.value,
        start: "top 80%",
        toggleActions: "play none none none"
      }
    }
  );

  // Animate button with matching timing
  gsap.fromTo(
    buttonRef.value,
    { opacity: 0, y: 30 },
    { 
      opacity: 1, 
      y: 0,
      duration: 0.8,
      delay: 0.4,
      ease: "power2.out",
      scrollTrigger: {
        trigger: showcaseRef.value,
        start: "top 80%",
        toggleActions: "play none none none"
      }
    }
  );

  // Animate bento grid with smooth fade-in - matching other components
  gsap.fromTo(
    bentoContainerRef.value,
    { opacity: 0, y: 30 },
    {
      opacity: 1,
      y: 0,
      duration: 0.8,
      delay: 0.6,
      ease: "power2.out",
      scrollTrigger: {
        trigger: bentoContainerRef.value,
        start: "top 85%",
        toggleActions: "play none none none"
      }
    }
  );
  
  // Add touch swipe functionality for mobile
  let touchStartX = 0;
  let touchEndX = 0;
  
  const handleTouchStart = (e) => {
    touchStartX = e.changedTouches[0].screenX;
  };
  
  const handleTouchEnd = (e) => {
    touchEndX = e.changedTouches[0].screenX;
    handleSwipe();
  };
  
  const handleSwipe = () => {
    const swipeThreshold = 50;
    if (touchEndX < touchStartX - swipeThreshold) {
      navigateNext();
    }
    if (touchEndX > touchStartX + swipeThreshold) {
      navigatePrev();
    }
  };
  
  // Add touch event listeners to the container
  const container = bentoContainerRef.value;
  if (container) {
    container.addEventListener('touchstart', handleTouchStart, false);
    container.addEventListener('touchend', handleTouchEnd, false);
  }
  
  // Clean up event listeners on unmount
  onUnmounted(() => {
    if (container) {
      container.removeEventListener('touchstart', handleTouchStart, false);
      container.removeEventListener('touchend', handleTouchEnd, false);
    }
  });
});
</script>

<template>
  <div ref="showcaseRef" class="relative w-full overflow-visible">
    <div class="wide-container flex flex-col">
      <div class="h-full w-full">
        <!-- Title section -->
        <div class="flex flex-col lg:flex-row items-start lg:items-end lg:justify-between w-full mb-12 gap-4">
          <hgroup class="flex flex-col grow">
            <h2 ref="titleRef" class="text-pretty text-5xl font-semibold text-jet sm:text-6xl lg:text-7xl tracking-tight">{{ title }}</h2>
            <p ref="subtitleRef" class="text-balance text-left text-xl sm:font-medium text-jet/80 max-w-6xl mt-6">{{ subtitle }}</p>
          </hgroup>
          
          <Button ref="buttonRef" href="/products" variant="white" icon="arrow-right" :block="true">
            Projecten vol slimme oplossingen
          </Button>
        </div>
        
        <!-- Bento Grid Layout - Using Tailwind classes instead of inline styles -->
        <div ref="bentoContainerRef" class="grid grid-cols-1 lg:grid-cols-3 gap-2 relative h-[80vh] min-h-[600px] max-h-[800px] grid-rows-[40fr,55fr]">
          <!-- First row: 1/3 + 2/3 width blocks - now using full height of grid cell -->
          <div class="lg:col-span-1 h-full">
            <CustomLink :href="!displayProducts[0].isPlaceholder ? `/project/${displayProducts[0].meta.slug}` : '#'" 
              :useInertia="!displayProducts[0].isPlaceholder" 
              class="group/card w-full h-full rounded-xl overflow-hidden relative flex flex-col justify-between shadow-md hover:shadow-xl transition-all duration-500" 
              :class="{ 'pointer-events-none opacity-50': displayProducts[0].isPlaceholder }"
              @mousemove="(e) => trackMousePosition(e, 0)"
              @mouseleave="resetHover"
            >
              <!-- Background Image & Overlay -->
              <img 
                :src="getProductImage(displayProducts[0])" 
                :alt="displayProducts[0].meta.name" 
                class="absolute inset-0 w-full h-full object-cover transition-opacity duration-700"
                :class="[`bg-gradient-to-tr from-${displayProducts[0].style.projectColor}-200`, `to-${displayProducts[0].style.projectColor}-400`]"
              />
              <div class="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent transition-opacity duration-300 group-hover/card:opacity-90"></div>
              
              <!-- Logo in top left - fades out on hover -->
              <div class="z-10 p-5 transition-opacity duration-300 group-hover/card:opacity-0">
                <img v-if="!displayProducts[0].isPlaceholder && displayProducts[0].meta.logo" 
                     :src="displayProducts[0].meta.logo" 
                     :alt="displayProducts[0].meta.name" 
                     class="h-16 w-auto object-contain drop-shadow-md"
                />
              </div>
              
              <!-- Content in bottom left - fades out on hover -->
              <div class="z-10 p-6 text-white transition-opacity duration-300 group-hover/card:opacity-0">
                <h3 class="text-xl font-bold">{{ displayProducts[0].meta.name }}</h3>
                <p class="text-sm text-white/80 line-clamp-2 mt-2">{{ displayProducts[0].content.subTitle }}</p>
              </div>
              
              <!-- Centered hover CTA - appears on hover -->
              <div class="absolute inset-0 z-20 flex items-center justify-center opacity-0 group-hover/card:opacity-100 transition-all duration-500">
                <span class="text-white font-medium text-4xl">
                {{ displayProducts[0].content.hoverCta || 'Meer info' }}
                </span>
              </div>
              
              <!-- Cursor-following arrow -->
              <div v-show="isHovering" 
                   class="absolute z-30 pointer-events-none transition-opacity duration-300 group-hover/card:opacity-100 opacity-0" 
                   :style="`left: ${mouseX}px; top: ${mouseY}px; transform: translate(-50%, -50%);`">
                <div class="flex items-center justify-center h-12 w-12 bg-white rounded-full shadow-md text-orange-600">
                  <font-awesome-icon :icon="['fas', 'arrow-right']" class="h-5 w-5" />
                </div>
              </div>
            </CustomLink>
          </div>
          
          <div class="lg:col-span-2 h-full">
            <CustomLink 
              :href="!displayProducts[1].isPlaceholder ? `/project/${displayProducts[1].meta.slug}` : '#'" 
              :useInertia="!displayProducts[1].isPlaceholder"
              class="group/card w-full h-full rounded-xl overflow-hidden relative flex flex-col justify-between shadow-md hover:shadow-xl transition-all duration-500"
              :class="{ 'pointer-events-none opacity-50': displayProducts[1].isPlaceholder }"
              @mousemove="(e) => trackMousePosition(e, 1)"
              @mouseleave="resetHover"
            >
              <!-- Background Image & Overlay -->
              <img 
                :src="getProductImage(displayProducts[1])" 
                :alt="displayProducts[1].meta.name" 
                class="absolute inset-0 w-full h-full object-cover transition-opacity duration-700"
                :class="[`bg-gradient-to-tr from-${displayProducts[1].style.projectColor}-200`, `to-${displayProducts[1].style.projectColor}-400`]"
              />
              <div class="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent transition-opacity duration-300 group-hover/card:opacity-90"></div>
              
              <!-- Logo in top left - fades out on hover -->
              <div class="relative z-10 p-5 transition-opacity duration-300 group-hover/card:opacity-0">
                <img v-if="!displayProducts[1].isPlaceholder && displayProducts[1].meta.logo" 
                     :src="displayProducts[1].meta.logo" 
                     :alt="displayProducts[1].meta.name" 
                     class="h-16 w-auto object-contain drop-shadow-md"
                />
              </div>
              
              <!-- Content in bottom left - fades out on hover -->
              <div class="relative z-10 p-6 text-white transition-opacity duration-300 group-hover/card:opacity-0">
                <h3 class="text-xl font-bold">{{ displayProducts[1].meta.name }}</h3>
                <p class="text-sm text-white/80 line-clamp-2 mt-2">{{ displayProducts[1].content.subTitle }}</p>
              </div>
              
              <!-- Centered hover CTA - appears on hover -->
              <div class="absolute inset-0 z-20 flex items-center justify-center opacity-0 group-hover/card:opacity-100 transition-all duration-500">
                <span class="text-white font-medium text-4xl">
                  {{ displayProducts[1].content.hoverCta || 'Meer info' }}
                </span>
              </div>
              
              <!-- Cursor-following arrow -->
              <div v-show="isHovering && currentCard === 1" 
                   class="absolute z-30 pointer-events-none transition-opacity duration-300 group-hover/card:opacity-100 opacity-0" 
                   :style="`left: ${mouseX}px; top: ${mouseY}px; transform: translate(-50%, -50%);`">
                <div class="flex items-center justify-center h-12 w-12 bg-white rounded-full shadow-md text-orange-600">
                  <font-awesome-icon :icon="['fas', 'arrow-right']" class="h-5 w-5" />
                </div>
              </div>
            </CustomLink>
          </div>
          
          <!-- Second row: full width block -->
          <div class="lg:col-span-3 h-full">
            <CustomLink 
              :href="!displayProducts[2].isPlaceholder ? `/project/${displayProducts[2].meta.slug}` : '#'" 
              :useInertia="!displayProducts[2].isPlaceholder"
              class="group/card w-full h-full rounded-xl overflow-hidden relative flex flex-col justify-between shadow-md hover:shadow-xl transition-all duration-500"
              :class="{ 'pointer-events-none opacity-50': displayProducts[2].isPlaceholder }"
              @mousemove="(e) => trackMousePosition(e, 2)"
              @mouseleave="resetHover"
            >
              <!-- Background Image & Overlay with large hero-style layout -->
              <img 
                :src="getProductImage(displayProducts[2])" 
                :alt="displayProducts[2].meta.name" 
                class="absolute inset-0 w-full h-full object-cover transition-opacity duration-700"
                :class="[`bg-gradient-to-tr from-${displayProducts[2].style.projectColor}-200`, `to-${displayProducts[2].style.projectColor}-400`]"
              />
              <div class="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent transition-opacity duration-300 group-hover/card:opacity-90"></div>
              
              <!-- Logo in top left - fades out on hover -->
              <div class="relative z-10 p-5 transition-opacity duration-300 group-hover/card:opacity-0">
                <img v-if="!displayProducts[2].isPlaceholder && displayProducts[2].meta.logo" 
                     :src="displayProducts[2].meta.logo" 
                     :alt="displayProducts[2].meta.name" 
                     class="h-16 w-auto object-contain drop-shadow-md"
                />
              </div>
              
              <!-- Content in bottom left - fades out on hover -->
              <div class="relative z-10 p-6 text-white transition-opacity duration-300 group-hover/card:opacity-0">
                <h3 class="text-2xl font-bold">{{ displayProducts[2].meta.name }}</h3>
                <p class="text-sm text-white/80 line-clamp-2 mt-2">{{ displayProducts[2].content.subTitle }}</p>
              </div>
              
              <!-- Centered hover CTA - appears on hover -->
              <div class="absolute inset-0 z-20 flex items-center justify-center opacity-0 group-hover/card:opacity-100 transition-all duration-500">
                <span class="text-white font-medium text-4xl">
                  {{ displayProducts[2].content.hoverCta || 'Meer info' }}
                </span>
              </div>
              
              <!-- Cursor-following arrow -->
              <div v-show="isHovering && currentCard === 2" 
                   class="absolute z-30 pointer-events-none transition-opacity duration-300 group-hover/card:opacity-100 opacity-0" 
                   :style="`left: ${mouseX}px; top: ${mouseY}px; transform: translate(-50%, -50%);`">
                <div class="flex items-center justify-center h-12 w-12 bg-white rounded-full shadow-md text-orange-600">
                  <font-awesome-icon :icon="['fas', 'arrow-right']" class="h-5 w-5" />
                </div>
              </div>
            </CustomLink>
          </div>
        </div>
        
        <!-- Navigation controls - Reduced top margin -->
        <div class="mt-3 flex justify-between items-center gap-4">
            
            <!-- Pagination indicators -->
            <div class="flex gap-2">
              <span v-for="i in totalSets" :key="i" :class="`block h-1 md:h-2 w-4 md:w-8 rounded-xs transition-all duration-300 cursor-pointer ${i-1 === currentSet ? 'bg-orange-200 w-4 md:w-8' : 'bg-gray-100 hover:bg-orange-100'}`" @click="!isAnimating && (currentSet = i-1)"></span>
            </div>
          <div class="flex gap-3 items-center">
            <button @click="navigatePrev" 
                    class="h-10 w-10 flex items-center justify-center rounded-full transition-all duration-300 bg-black text-gray-200 hover:bg-gray-800 disabled:opacity-50"
                    :class="currentSet === 0 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-800'"
                    aria-label="Previous projects">
              <font-awesome-icon :icon="['fas', 'chevron-left']" class="h-4 w-4" />
            </button>
            
            <button @click="navigateNext" 
                    class="h-10 w-10 flex items-center justify-center rounded-full transition-all duration-300 bg-black text-gray-200 hover:bg-gray-800 disabled:opacity-50"
                    :class="currentSet >= totalSets - 1 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-800'"
                    aria-label="Next projects">
              <font-awesome-icon :icon="['fas', 'chevron-right']" class="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style>
/* Some effects can't be directly created with Tailwind classes and need to remain as CSS */
@layer utilities {
  /* Card 3D transform handling - can be added to Tailwind config if used often */
  .transform-style-3d { transform-style: preserve-3d; }
  .backface-hidden { backface-visibility: hidden; }
  .will-change-transform { will-change: transform; }
  
  /* Apply these utility classes to the card in the template */
}

/* Add smooth movement for cursor arrow */
.absolute.z-30 {
  transition: left 0.1s ease-out, top 0.1s ease-out;
}
</style>
